import { useMutation, useQuery } from '@tanstack/react-query';
import type { AppSettingsResponse, BaseLocation, BaseLocationRequest, GeoPlaceDto } from '@kominiarz/shared';
import { api } from './api';
import { useDebounced } from './clientsApi';
import { queryClient } from './queryClient';

export const SETTINGS_KEY = ['settings'] as const;
export const MIN_ADDRESS_CHARS = 3;
/** Map centre when no base is set: the middle of Poland. */
export const POLAND_CENTER = { lat: 52.07, lng: 19.48, zoom: 6 };

export function useAppSettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api<AppSettingsResponse>('/settings'),
    staleTime: 10 * 60_000,
  });
}

/** Suggestions while typing; requests start 300 ms after the last keystroke. */
export function useAddressSuggestions(query: string, enabled: boolean) {
  const q = useDebounced(query.trim(), 300);
  return useQuery({
    queryKey: ['geo', 'suggest', q],
    queryFn: ({ signal }) =>
      api<{ places: GeoPlaceDto[]; available: boolean }>(`/geo/suggest?q=${encodeURIComponent(q)}`, { signal }),
    enabled: enabled && q.length >= MIN_ADDRESS_CHARS,
    staleTime: 5 * 60_000,
  });
}

export function geocodeAddress(address: string) {
  return api<{ place: GeoPlaceDto | null; available: boolean }>(`/geo/geocode?address=${encodeURIComponent(address)}`);
}

export function useSaveBaseLocation() {
  return useMutation({
    mutationFn: (body: BaseLocationRequest) =>
      api<{ baseLocation: BaseLocation }>('/settings/base-location', { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}
